import React, { useState, useEffect } from "react";
import { useIntl } from "react-intl";
import PropTypes from "prop-types";
import { TextCell } from "./TextCell";
import { TitleCell } from "./TitleCell";
import { InputCell } from "./InputCell";
import { DropdownCell } from "./DropdownCell";
import { TextCellAligned } from "./TextCellAligned";
import { MathJaxCell } from "./MathJaxCell";
import { ImageCell } from "./ImageCell";
import { CELL_TYPES, DATA_TYPE } from "./DataTable.constants";
import "./style.scss";

export function DataTable(props) {
  // On mount calculate the number of inputs
  const [numberOfInputs, setNumberOfInputs] = useState(0);
  const { cellHeight } = props;

  useEffect(() => {
    let inputCount = 0;
    props.data.map((row) => {
      row.map((cell) => {
        switch (cell.type) {
          case CELL_TYPES.DROPDOWN:
            return inputCount++;
          case CELL_TYPES.INPUT:
            return inputCount++;
          default:
            return null;
        }
      });
    });
    setNumberOfInputs(inputCount);
  }, []);

  const onCellChange = (params) => props.onCellChange(params);

  const setIsCorrect = (cell) => {
    if (cell.value !== null && cell.value !== undefined && cell.value !== "") {
      // Get uniqueResponses from the store
      const uniqueResponses = props.uniqueResponses || [];

      // Check if cell.value is more than 1 time in unique responses
      if (
        cell.unique &&
        uniqueResponses.includes(cell.value) &&
        uniqueResponses.filter((value) => value === cell.value).length > 1
      ) {
        return false;
      }

      if (typeof cell.answer === DATA_TYPE.STRING) {
        return cell.answer.toLowerCase() === cell.value.toLowerCase();
      }
      if (typeof cell.answer === DATA_TYPE.NUMBER) {
        return cell.answer == cell.value;
      }
      if (
        typeof cell.answer === "object" &&
        cell.answer !== null &&
        Object.hasOwn(cell.answer, "min")
      ) {
        return cell.value >= cell.answer.min;
      }
      if (
        typeof cell.answer === "object" &&
        cell.answer !== null &&
        Object.hasOwn(cell.answer, "max")
      ) {
        return cell.value <= cell.answer.max;
      }
      if (Array.isArray(cell.answer)) {
        return cell.answer.includes(cell.value);
      }
      return false;
    } else {
      return false;
    }
  };

  const renderDropdown = (cell, cellId) =>
    React.createElement(DropdownCell, {
      key: cellId,
      placeholder: cell.placeholder,
      className: "dataTable_row_cell",
      options: cell.options,
      value: cell.value,
      id: cellId,
      onChange: onCellChange,
      disabled: cell.disabled,
      showFeedback: cell.showFeedback,
      unique: cell.unique ?? false,
      isCorrect: setIsCorrect(cell),
      feedback: cell.feedback,
      answer: cell.answer,
      colspan: cell.colspan,
      numberOfInputs: numberOfInputs,
      cellHeight: cellHeight,
    });

  const renderInputCell = (cell, cellId) =>
    React.createElement(InputCell, {
      key: cellId,
      id: cellId,
      placeholder: cell.placeholder,
      className: "dataTable_row_cell",
      type: cell.inputType,
      value: cell.value,
      onChange: onCellChange,
      disabled: cell.disabled,
      showFeedback: cell.showFeedback,
      unique: cell.unique ?? false,
      isCorrect: setIsCorrect(cell),
      feedback: cell.feedback,
      accuracy: cell.accuracy,
      answer: cell.answer,
      colspan: cell.colspan,
      numberOfInputs: numberOfInputs,
    });

  const renderFixedCell = (cell, cellId) =>
    React.createElement(TextCell, {
      key: cellId,
      id: cellId,
      className: `dataTable_row_${cell.type.toLowerCase()}`,
      text: cell.text,
      colspan: cell.colspan,
    });

  const renderMathJax = (cell, cellId) =>
    React.createElement(MathJaxCell, {
      key: cellId,
      id: cellId,
      className: `dataTable_row_${cell.type.toLowerCase()}`,
      text: cell.text,
      colspan: cell.colspan,
    });

  const renderImageCell = (cell, cellId) =>
    React.createElement(ImageCell, {
      key: cellId,
      id: cellId,
      className: `dataTable_row_${cell.type.toLowerCase()}`,
      image: cell.image,
      alt: cell.alt || "",
      colspan: cell.colspan,
      caption: cell.caption,
      imageSize: cell.imageSize,
    });

  const renderTextAlignedCell = (cell, cellId) =>
    React.createElement(TextCellAligned, {
      key: cellId,
      id: cellId,
      className: `dataTable_row_textAligned`,
      text: cell.text,
      colspan: cell.colspan,
    });

  const renderTitleCell = (cell, cellId) =>
    React.createElement(TitleCell, {
      key: cellId,
      id: cellId,
      className: `dataTable_row_${cell.type.toLowerCase()}`,
      text: cell.text,
      colspan: cell.colspan,
    });

  const renderCellByType = (cell, cellId) => {
    const intl = useIntl();
    cell = {
      ...cell,
      placeholder: cell.placeholder
        ? intl.formatMessage({
            id: cell.placeholder,
            defaultMessage: cell.placeholder,
          })
        : cell.placeholder,
      text: cell.text
        ? intl.formatMessage({ id: cell.text, defaultMessage: cell.text })
        : cell.text,
    };

    switch (cell.type) {
      case CELL_TYPES.DROPDOWN:
        return renderDropdown(cell, cellId);
      case CELL_TYPES.INPUT:
        return renderInputCell(cell, cellId);
      case CELL_TYPES.TITLE:
        return renderTitleCell(cell, cellId);
      case CELL_TYPES.FIXED:
        return renderFixedCell(cell, cellId);
      case CELL_TYPES.TEXT_ALIGNED:
        return renderTextAlignedCell(cell, cellId);
      case CELL_TYPES.MATH_JAX:
        return renderMathJax(cell, cellId);
      case CELL_TYPES.IMAGE:
      case CELL_TYPES.IMAGE_TITLE:
        return renderImageCell(cell, cellId);
      default:
        return null;
    }
  };

  const renderCell = (rowIndex) => (cell, colIndex) =>
    renderCellByType(cell, `${rowIndex}_${colIndex}`);

  const renderRow = (row, key) => (
    <tr key={key} className="dataTable_row">
      {row.map(renderCell(key))}
    </tr>
  );

  return (
    <table className={`dataTable ${props.className}`}>
      <tbody className={props.className}>{props.data.map(renderRow)}</tbody>
    </table>
  );
}

DataTable.propTypes = {
  /**
   * Number of [rows, columns] in the table
   */
  indices: PropTypes.arrayOf(PropTypes.number),
  /**
   * Data to be rendered in the table
   */
  data: PropTypes.arrayOf(
    PropTypes.arrayOf(
      PropTypes.shape({
        /**
         * The type of the cell
         */
        type: PropTypes.oneOf([
          "TITLE",
          "FIXED",
          "INPUT",
          "DROPDOWN",
          "TEXT_ALIGNED",
        ]),
        /**
         * Text to render in cell [ TITLE, FIXED]
         */
        text: PropTypes.text,
        /**
         * Type of the input [ INPUT ]
         */
        inputType: PropTypes.oneOf(["num", "text"]),
        /**
         * placeholder of for cell [ DROPDOWN, INPUT]
         */
        placeholder: PropTypes.string,
        /**
         * options to display on dropdown [DROPDOWN]
         */
        options: PropTypes.array,
        /**
         * Boolean to disable the cell
         */
        disabled: PropTypes.bool,
        /**
         * Boolean to set if feedback should be displayed
         */
        showFeedback: PropTypes.bool,
        /**
         * Boolean to set if feedback should be displayed
         */
        unique: PropTypes.bool,
        /**
         * Sets the number of fractional didgets to which a number is accurate
         */
        accuracy: PropTypes.number,
      })
    )
  ),
  /**
   * Function to invoke when the data in a cell changes
   */
  onCellChange: PropTypes.func,
  /**
   * ClassName for the DataTable
   */
  className: PropTypes.string,
  /**
   * Array of unique values to check for duplicates
   */
  uniqueResponses: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ),
};

DataTable.defaultProps = {
  className: "",
  uniqueResponses: [],
};
